import { Component, EventEmitter, Input, Output, HostListener, ElementRef  } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Keyboard } from '@capacitor/keyboard';
import { Platform } from '@ionic/angular';

@Component({
  selector: 'app-user-selector',
  templateUrl: './user-selector.component.html',
  styleUrls: ['./user-selector.component.scss']
})
export class UserSelectorComponent {
  @Input() label: string;
  @Input() form: UntypedFormGroup;
  @Input() placeholder: string = 'Search users';
  @Input() users: any[] = [];
  @Input() selectedUsers: any[] = [];
  @Output() usersSelected = new EventEmitter<any[]>();
  @Input() selectionMode: 'single' | 'multiple' = 'multiple';
  @Input() isPredefined: boolean = false;
  
  searchText: string = '';
  filteredUsers: any[] = [];
  isPopoverOpen: boolean = false;
  isSelectingUser: boolean = false;

  constructor(private elementRef: ElementRef, private platform: Platform) {}

  ngOnInit() {
    this.filteredUsers = [...this.users];
  }

  @HostListener('document:click', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  onClickOutside(event: MouseEvent | TouchEvent) {
    if (this.isSelectingUser) {
      return;
    }
    if (this.isPopoverOpen) {
      const target = event.target as HTMLElement;
      const clickedInside = this.elementRef.nativeElement.contains(event.target);
      const clickedOnPopover = target.closest('.search-results-popover');
      if (!clickedInside || (clickedInside && !clickedOnPopover && !target.closest('ion-searchbar'))) {
        this.closePopover();
      }
    }
  }

  // ionInput fires on every keystroke / deletion (ionChange only fires on Enter or blur in Ionic 7+)
  onSearchInput(event?: any) {
    this.searchText = event?.detail?.value ?? '';
    this.filterUsers();
    this.openPopover();
  }

  filterUsers() {
    const search = (this.searchText || '').trim().toLowerCase();
    this.filteredUsers = search === ''
      ? [...this.users]
      : this.users.filter(user => user.userName?.toLowerCase().includes(search));
  }

  openPopover() {
    this.isPopoverOpen = true;
  }

  closePopover() {
    this.isPopoverOpen = false;
    this.isSelectingUser = false;
  }

  // selectUser(user: any) {
  //   this.isSelectingUser = true;
  //   setTimeout(() => {
  //     if (!this.isSelected(user)) {
  //         if (this.selectionMode === 'single') {
  //             this.selectedUsers = [user];
  //         } else {
  //             this.selectedUsers.push(user);
  //         }
  //         this.usersSelected.emit([...this.selectedUsers]);
  //     }
  //     this.searchText = '';
  //     this.filterUsers();
  //     this.closePopover();
  //   }, 100);
  // }

  async selectUser(user: any, event?: Event) {
    if (this.isSelectingUser) {
      return;
    }
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.isSelectingUser = true;
    if (this.platform.is('ios') || this.platform.is('android') || this.platform.is('capacitor')) {
      try {
        await Keyboard.hide();
      } catch (error) {
        console.log('Keyboard hide error:', error);
      }
    }

    const delay = (this.platform.is('ios') || this.platform.is('android') || this.platform.is('capacitor')) ? 100 : 0;

    setTimeout(() => {
      if (!this.isSelected(user)) {
        if (this.selectionMode === 'single') {
          this.selectedUsers = [user];
        } else {
          this.selectedUsers.push(user);
        }
        this.usersSelected.emit([...this.selectedUsers]);
      }
      this.searchText = '';
      this.filterUsers();
      this.closePopover();
    }, delay);
  }

  removeUser(user: any) {
    this.selectedUsers = this.selectedUsers.filter(u => u.userId !== user.userId);
    this.usersSelected.emit(this.selectedUsers);
  }

  isSelected(user: any): boolean {
    return this.selectedUsers.some(u => u.userId === user.userId);
  }

  async onSearchbarClick() {
    if (this.isPopoverOpen) {
      this.closePopover();
      if (this.platform.is('capacitor')) {
        try {
          await Keyboard.hide();
        } catch (error) {
          console.log('Keyboard hide error:', error);
        }
      }
    } else {
      this.filterUsers();
      this.openPopover();
    }
  }

  onSearchbarBlur() {
    if (this.isSelectingUser) {
      return;
    }
    const delay = (this.platform.is('ios') || this.platform.is('android') || this.platform.is('capacitor')) ? 200 : 150;
    setTimeout(() => {
      if (this.isSelectingUser) {
        return;
      }
      const activeElement = document.activeElement;
      if (!activeElement?.closest('.search-results-popover')) {
        this.closePopover();
      }
    }, delay);
  }
}